import { useEffect, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { useNavigate } from "react-router";
import { useSetAtom, useAtomValue } from "jotai";
import { loginAction } from "../../stores/auth";
import { consumePostAuthReturn } from "../../lib/githubAuth";
import { localeAtom } from "../../stores/locale";
import { useT } from "../../lib/i18n";
import { track } from "../../lib/posthog";

export default function Callback() {
  const navigate = useNavigate();
  const login = useSetAtom(loginAction);
  const locale = useAtomValue(localeAtom);
  const t = useT(locale);
  // failed=true when the callback carried no access token. Rather than bouncing
  // to /login silently (the user never learns sign-in didn't complete), we show
  // a localized message, then redirect after a short beat so they can read it.
  const [failed, setFailed] = useState(false);

  // Capture the tokens ONCE, at first render, before any effect strips the
  // fragment. The effect below clears the fragment as a side effect; under
  // React StrictMode the effect runs twice in dev, so a second run that
  // re-read window.location.hash would find it already emptied and bounce to
  // /login even though login succeeded. Memoising via the useState initializer
  // (same pattern as rhf defaultValues) makes both runs see the same tokens.
  //
  // Tokens are delivered in the URL fragment (#...) so they never reach
  // server / CDN access logs or the Referer header. Fall back to the query
  // string for backward compatibility with any in-flight old redirect.
  const [params] = useState(() => {
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : "";
    return new URLSearchParams(hash || window.location.search);
  });

  useEffect(() => {
    // Strip the fragment so the tokens don't linger in the address bar / history.
    if (window.location.hash) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }

    const token = params.get("access_token");
    const refreshToken = params.get("refresh_token");

    if (token) {
      const userId = Number(params.get("user_id")) || 0;
      const userLogin = params.get("login") || "";
      login({
        access_token: token,
        refresh_token: refreshToken || "",
        user: {
          id: userId,
          login: userLogin,
          avatar_url: params.get("avatar_url") || "",
        },
      });
      track.signedUp(userId, userLogin);
      // A re-auth started from a deep page (e.g. the Repos token-expired
      // banner) stashes the path to return to client-side, since the OAuth
      // start route can't round-trip an arbitrary redirect through GitHub.
      // Always consume it so it can't leak into a later login; the backend
      // `redirect` fragment (App-install flow) still wins when present.
      const storedReturn = consumePostAuthReturn();
      const redirect = params.get("redirect") ?? storedReturn ?? undefined;
      if (redirect) {
        // Galaxy-scoped routes live under /:account/...
        // If redirect is a bare sub-path like "/control-room", prefix with the user's login
        // so it resolves to "/:account/control-room" instead of matching /:account with "control-room" as the account name.
        const TOP_LEVEL_PATHS = ["/", "/lp", "/login", "/auth/callback"];
        const isTopLevel =
          TOP_LEVEL_PATHS.includes(redirect) ||
          redirect.startsWith("/invite/");
        const isBareSubPath =
          redirect.startsWith("/") &&
          !redirect.startsWith("//") &&
          !redirect.includes("/", 1);

        const target =
          !isTopLevel && isBareSubPath && userLogin
            ? `/${userLogin}${redirect}`
            : redirect;
        navigate(target);
      } else {
        navigate("/");
      }
    } else {
      // No token in the callback — sign-in didn't complete. Surface a localized
      // message, then redirect to /login so the failure isn't silent.
      setFailed(true);
      const timer = window.setTimeout(() => navigate("/login"), 2500);
      return () => window.clearTimeout(timer);
    }
  }, [navigate, login, params]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gruvbox-bg">
      <div className="text-center">
        {failed ? (
          <p className="text-gruvbox-red">{t("auth.failed")}</p>
        ) : (
          <>
            <div className="mb-lg text-gruvbox-yellow"><LoaderCircle size={28} className="mx-auto animate-spin" /></div>
            <p className="text-gruvbox-gray">{t("auth.authenticating")}</p>
          </>
        )}
      </div>
    </div>
  );
}
